// Phase 8A Step 6 — Opening Context Card (§4.9).
//
// Shown at the top of a fresh conversation to ground SIKA with the
// user's current state: bankroll, active goals, chain position.
// Appears as a compact card above the first message.

import { Icon } from "../../components/icons/Icon.tsx";
import type { SikaGoalRow } from "../../sika/useSikaGoals.ts";

interface Props {
  bankroll?: number;
  goals: SikaGoalRow[];
  chainDay?: number | null;
  chainStatus?: "active" | "reset" | "none";
}

const fmtAmt = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: 0 });

export function SikaOpeningCard({
  bankroll = 0,
  goals,
  chainDay,
  chainStatus = "none",
}: Props): JSX.Element {
  const activeGoals = goals.filter(
    (g) => g.status === "active" || g.status === "in_progress",
  );

  return (
    <div className="opening-card">
      <div className="opening-card-head">
        <Icon name="bolt" size={12} />
        <span>Your current context</span>
      </div>

      <div className="opening-card-stats">
        <div className="opening-stat">
          <span className="opening-stat-label">Bankroll</span>
          <span className="opening-stat-value">GH₵ {fmtAmt(bankroll)}</span>
        </div>

        <div className="opening-stat">
          <span className="opening-stat-label">Active goals</span>
          <span className="opening-stat-value">{activeGoals.length}</span>
        </div>

        {chainDay != null && chainStatus !== "none" && (
          <div className="opening-stat">
            <span className="opening-stat-label">Chain</span>
            <span className={`opening-stat-value ${chainStatus === "active" ? "pos" : "neg"}`}>
              {chainStatus === "active" ? `Day ${chainDay}` : "Reset"}
            </span>
          </div>
        )}
      </div>

      {activeGoals.length > 0 && (
        <div className="opening-goals">
          {activeGoals.slice(0, 3).map((g) => (
            <div key={g.id} className="opening-goal-chip">
              <span className="opening-goal-name">{g.title}</span>
              <span className="opening-goal-pct">
                {g.target_amount > 0
                  ? `${Math.round((g.current_amount / g.target_amount) * 100)}%`
                  : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
